import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import classNames from "classnames";
import TrackedButton from "containers/TrackedButton";
import { MutableRefObject, useRef, useState } from "react";

import styles from "./styles.module.scss";

export interface IMenuDropdown {
    /**
     * Options that will appear in dropdown.
     */
    options: WhiteDropdownMenuOptions[];
    /**
     * Text that will appear on button.
     */
    buttonName: string;
    /**
     * If there is to be a icon right before the text.
     */
    btnIcon?: JSX.Element;
    style?: React.CSSProperties;
    /**
     * Action for the main button. If no action is passed, it simply opens the menu.
     */
    mainAction?: (() => void) | undefined;
    /**
     * Action triggered when they click on any option.
     */
    onMenuClick?: () => void;
    /**
     * Min width of text part of button.
     */
    minWidth?: string;
    disableMainAction?: boolean;
    tooltipText?: string;
}

export interface WhiteDropdownMenuOptions {
    /**
     * Needed for iteration key.
     */
    id: number;
    /**
     * Label of the option.
     */
    name: string;
    /**
     * Action triggered when clicking on the button.
     */
    action: (arg?: any) => void;
    disabled?: boolean;
    /**
     * Add a tooltip to the action
     */
    tooltip?: string;
    hide?: boolean;
    trackedEvent?: string;
}

const WhiteDropdownMenu = ({
    options,
    buttonName,
    btnIcon,
    style,
    mainAction,
    onMenuClick,
    minWidth,
    disableMainAction,
    tooltipText,
}: IMenuDropdown) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const arrowBtnRef = useRef(null);

    const handleMainAction = (e) => {
        onMenuClick && onMenuClick();
        if (!mainAction) {
            arrowBtnRef.current && (arrowBtnRef.current as any).click();
        } else {
            mainAction();
        }
    };

    const handleExpandBtn = (event: React.MouseEvent<HTMLDivElement> | any) => {
        onMenuClick && onMenuClick();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (option: any) => {
        option.action();
        setAnchorEl(null);
    };

    const _options = options.filter((option) => !option.hide);

    return (
        <>
            <button className={styles["white-dropdown-btn"]} style={{ ...style, minWidth }}>
                <Tooltip title={disableMainAction && tooltipText ? tooltipText : ""} arrow>
                    <div
                        className={classNames(
                            {
                                [styles["btn-text"]]: true,
                                [styles["disabled"]]: disableMainAction,
                            },
                            "whitespace-nowrap",
                        )}
                        onClick={(e) => (disableMainAction ? {} : handleMainAction(e))}
                    >
                        {btnIcon && btnIcon}
                        {buttonName}
                    </div>
                </Tooltip>
                <div
                    className={classNames({
                        [styles["btn-arrow"]]: true,
                        [styles["disabled"]]: _options.length === 0,
                    })}
                    onClick={handleExpandBtn}
                    ref={arrowBtnRef}
                >
                    <ExpandMoreIcon />
                </div>
            </button>
            {_options.length > 0 && (
                <Menu
                    id="menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    className={styles["insert-variable-menu"]}
                    elevation={0}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    {_options.map((option) => {
                        const display = !option.hide;
                        const ItemComponent = (
                            <MenuItem
                                key={option.id}
                                onClick={() => handleClose(option)}
                                disabled={option.disabled}
                                sx={{
                                    fontSize: "13px",
                                    fontFamily: "Poppins",
                                    borderBottom: "1px solid rgb(234, 234, 234)",
                                }}
                            >
                                {option.name}
                            </MenuItem>
                        );

                        return (
                            display && (
                                <Tooltip title={option?.tooltip} style={{ cursor: "pointer" }} key={option.id} arrow>
                                    {option.trackedEvent ? (
                                        <TrackedButton action={option.trackedEvent}>{ItemComponent}</TrackedButton>
                                    ) : (
                                        <div>{ItemComponent}</div>
                                    )}
                                </Tooltip>
                            )
                        );
                    })}
                </Menu>
            )}
        </>
    );
};

export { WhiteDropdownMenu };
